import { Suspense, lazy } from "react";
import { createBrowserRouter } from "react-router-dom";

import LoadingFallback from "@/components/Loading";
import RouteError from "@/components/RouteError";
import { ROUTES } from "@/constants";
import Layout from "@/layout";

import ProtectedRoute from "./ProtectedRoute";
import RoleGuard from "./RoleGuard";

const Login = lazy(() => import("@/pages/Login"));
const Home = lazy(() => import("@/pages/Home"));
const Users = lazy(() => import("@/pages/Users"));
const Agents = lazy(() => import("@/pages/Users/Agents"));
const UserProfile = lazy(() => import("@/pages/Users/Profile"));

const Tenants = lazy(() => import("@/pages/Foundation/Tenants"));
const Templates = lazy(() => import("@/pages/Template/Templates"));
const Brands = lazy(() => import("@/pages/Foundation/Brands"));

const BaseLanguages = lazy(() => import("@/pages/Base/Languages"));
const BaseCurrencies = lazy(() => import("@/pages/Base/Currencies"));
const BaseCountries = lazy(() => import("@/pages/Base/Countries"));
const BaseTranslations = lazy(() => import("@/pages/Base/Translations"));
const Roles = lazy(() => import("@/pages/Users/Roles"));

const PaymentProviders = lazy(() => import("@/pages/Payment/Providers"));
const PaymentWithdrawals = lazy(() => import("@/pages/Payment/Withdrawals"));
const PaymentMethodRules = lazy(() => import("@/pages/Payment/MethodRules"));

const ComplianceKyc = lazy(() => import("@/pages/Compliance/Kyc"));
const ComplianceRisk = lazy(() => import("@/pages/Compliance/Risk"));
const ComplianceBetLimits = lazy(() => import("@/pages/Compliance/BetLimits"));
const ComplianceChat = lazy(() => import("@/pages/Compliance/Chat"));

const GameAggregators = lazy(() => import("@/pages/Game/Aggregators"));
const GameCategories = lazy(() => import("@/pages/Game/Categories"));
const GameTypes = lazy(() => import("@/pages/Game/GameTypes"));
const GameProviders = lazy(() => import("@/pages/Game/Providers"));
const GameBots = lazy(() => import("@/pages/Game/Bots"));
const Games = lazy(() => import("@/pages/Game/Games"));

const BrandSidebar = lazy(() => import("@/pages/Brand/Sidebar"));
const BrandBanner = lazy(() => import("@/pages/Brand/Banner"));
const BrandFooterList = lazy(() => import("@/pages/Brand/Footer/List"));
const BrandFooterItem = lazy(() => import("@/pages/Brand/Footer/Item"));
const BrandFooterImage = lazy(() => import("@/pages/Brand/Footer/Image"));

const TemplateSidebar = lazy(() => import("@/pages/Template/Sidebar"));
const TemplateBanner = lazy(() => import("@/pages/Template/Banner"));
const TemplateFaq = lazy(() => import("@/pages/Template/Faq"));

const FooterList = lazy(() => import("@/pages/Template/Footer/List"));
const FooterItem = lazy(() => import("@/pages/Template/Footer/Item"));
const FooterImage = lazy(() => import("@/pages/Template/Footer/Image"));

const CrmNotification = lazy(() => import("@/pages/Crm/Notification"));
const CrmSupportHistory = lazy(() => import("@/pages/Crm/SupportHistory"));
const CrmHelpCategories = lazy(() => import("@/pages/Crm/HelpCenter/Categories"));
const CrmHelpArticles = lazy(() => import("@/pages/Crm/HelpCenter/Articles"));
const CrmSitePages = lazy(() => import("@/pages/Crm/SitePages"));
const CrmSupportWidget = lazy(() => import("@/pages/Crm/SupportWidget"));

const AnalyticsReport = lazy(() => import("@/pages/Report/Analytics"));
const ReportSchedules = lazy(() => import("@/pages/Report/Schedules"));
const BalanceAdjustments = lazy(() => import("@/pages/Payment/BalanceAdjustments"));
const SkySettings = lazy(() => import("@/pages/Payment/SkySettings"));
const SwapApprovals = lazy(() => import("@/pages/Payment/SwapApprovals"));
const SolvencyReport = lazy(() => import("@/pages/Report/Solvency"));
const GameReport = lazy(() => import("@/pages/Report/Game"));
const PaymentReport = lazy(() => import("@/pages/Report/Payment"));
const WalletLedgerReport = lazy(() => import("@/pages/Report/WalletLedger"));
const BonusGrantReport = lazy(() => import("@/pages/Report/BonusGrant"));
const BonusWalletLedgerReport = lazy(() => import("@/pages/Report/BonusWalletLedger"));
const DailySpinReport = lazy(() => import("@/pages/Report/DailySpin"));
const WagerRaceReport = lazy(() => import("@/pages/Report/WagerRace"));
const SlotBattleReport = lazy(() => import("@/pages/Report/SlotBattle"));
const RaffleReport = lazy(() => import("@/pages/Report/Raffle"));
const RakebackReport = lazy(() => import("@/pages/Report/Rakeback"));
const VipBoostReport = lazy(() => import("@/pages/Report/VipBoost"));
const DailyCheckinReport = lazy(() => import("@/pages/Report/DailyCheckin"));
const MissionReport = lazy(() => import("@/pages/Report/Mission"));
const LoyaltyStoreReport = lazy(() => import("@/pages/Report/LoyaltyStore"));
const TippingReport = lazy(() => import("@/pages/Report/Tipping"));

const PromotionTemplate = lazy(() => import("@/pages/Promotion/Template"));
const PromotionVipList = lazy(() => import("@/pages/Promotion/VipList"));
const PromotionVipSources = lazy(() => import("@/pages/Promotion/VipSources"));
const PromotionDailySpin = lazy(() => import("@/pages/Promotion/DailySpin"));
const PromotionJackpot = lazy(() => import("@/pages/Promotion/Jackpot"));
const PromotionWagerRace = lazy(() => import("@/pages/Promotion/WagerRace"));
const PromotionChallenge = lazy(() => import("@/pages/Promotion/Challenge"));
const PromotionRaffle = lazy(() => import("@/pages/Promotion/Raffle"));
const PromotionRakeback = lazy(() => import("@/pages/Promotion/Rakeback"));
const PromotionVipBoost = lazy(() => import("@/pages/Promotion/VipBoost"));
const PromotionVipHost = lazy(() => import("@/pages/Promotion/VipHost"));
const PromotionDailyCheckin = lazy(() => import("@/pages/Promotion/DailyCheckin"));
const PromotionMission = lazy(() => import("@/pages/Promotion/Mission"));
const PromotionLoyaltyStore = lazy(() => import("@/pages/Promotion/LoyaltyStore"));

const SidebarManage = lazy(() => import("@/pages/Permission/SidebarManage"));
const GroupManage = lazy(() => import("@/pages/Permission/GroupManage"));
const FunctionsManage = lazy(() => import("@/pages/Permission/FunctionsManage"));
const Icons = lazy(() => import("@/pages/Permission/Icons"));

const Sessions = lazy(() => import("@/pages/Sessions"));
const AuditLog = lazy(() => import("@/pages/Security/AuditLog"));
const Backups = lazy(() => import("@/pages/Security/Backups"));
const LoginHistory = lazy(() => import("@/pages/Security/LoginHistory"));
const TrackingWhitelist = lazy(() => import("@/pages/Security/TrackingWhitelist"));
const GeoAccess = lazy(() => import("@/pages/Security/GeoAccess"));
const ServerSwitches = lazy(() => import("@/pages/Security/ServerSwitches"));

const NotFound = lazy(() => import("@/pages/NotFound"));
const Forbidden = lazy(() => import("@/pages/Forbidden"));

const router = createBrowserRouter([
  {
    errorElement: <RouteError />,
    element: <Layout />,
    children: [
      {
        element: <ProtectedRoute />,
        children: [
          { path: ROUTES.FORBIDDEN, element: <Forbidden /> },
          {
            element: <RoleGuard />,
            errorElement: <RouteError />,
            children: [
              { path: ROUTES.HOME, element: <Home /> },
              { path: ROUTES.TENANTS, element: <Tenants /> },
              { path: ROUTES.TEMPLATES, element: <Templates /> },
              { path: ROUTES.BRANDS, element: <Brands /> },
              { path: ROUTES.BASE_LANGUAGES, element: <BaseLanguages /> },
              { path: ROUTES.BASE_CURRENCIES, element: <BaseCurrencies /> },
              { path: ROUTES.BASE_COUNTRIES, element: <BaseCountries /> },
              { path: ROUTES.BASE_TRANSLATIONS, element: <BaseTranslations /> },
              { path: ROUTES.ROLES, element: <Roles /> },
              { path: ROUTES.USERS, element: <Users /> },
              { path: ROUTES.AGENTS, element: <Agents /> },
              { path: ROUTES.USER_PROFILE, element: <UserProfile /> },
              { path: ROUTES.PAYMENT_PROVIDERS, element: <PaymentProviders /> },
              { path: ROUTES.PAYMENT_WITHDRAWALS, element: <PaymentWithdrawals /> },
              { path: ROUTES.PAYMENT_METHOD_RULES, element: <PaymentMethodRules /> },
              { path: ROUTES.PAYMENT_BALANCE_ADJUSTMENTS, element: <BalanceAdjustments /> },
              { path: ROUTES.PAYMENT_SKY_SETTINGS, element: <SkySettings /> },
              { path: ROUTES.PAYMENT_SWAP_APPROVALS, element: <SwapApprovals /> },
              { path: ROUTES.COMPLIANCE_KYC, element: <ComplianceKyc /> },
              { path: ROUTES.COMPLIANCE_RISK, element: <ComplianceRisk /> },
              { path: ROUTES.COMPLIANCE_CHAT, element: <ComplianceChat /> },
              { path: ROUTES.COMPLIANCE_BET_LIMITS, element: <ComplianceBetLimits /> },
              { path: ROUTES.BRAND_SIDEBAR, element: <BrandSidebar /> },
              { path: ROUTES.BRAND_BANNER, element: <BrandBanner /> },
              { path: ROUTES.BRAND_FOOTER_LIST, element: <BrandFooterList /> },
              { path: ROUTES.BRAND_FOOTER_ITEM, element: <BrandFooterItem /> },
              { path: ROUTES.BRAND_FOOTER_IMAGE, element: <BrandFooterImage /> },
              { path: ROUTES.GAME_AGGREGATORS, element: <GameAggregators /> },
              { path: ROUTES.GAME_CATEGORIES, element: <GameCategories /> },
              { path: ROUTES.GAME_TYPES, element: <GameTypes /> },
              { path: ROUTES.GAME_PROVIDERS, element: <GameProviders /> },
              { path: ROUTES.GAME_BOTS, element: <GameBots /> },
              { path: ROUTES.GAMES, element: <Games /> },
              { path: ROUTES.CMS_SIDEBAR, element: <TemplateSidebar /> },
              { path: ROUTES.CMS_BANNER, element: <TemplateBanner /> },
              { path: ROUTES.CMS_FAQ, element: <TemplateFaq /> },
              { path: ROUTES.TEMPLATE_FOOTER_LIST, element: <FooterList /> },
              { path: ROUTES.TEMPLATE_FOOTER_ITEM, element: <FooterItem /> },
              { path: ROUTES.TEMPLATE_FOOTER_IMAGE, element: <FooterImage /> },
              { path: ROUTES.CRM_NOTIFICATION, element: <CrmNotification /> },
              { path: ROUTES.CRM_SUPPORT_HISTORY, element: <CrmSupportHistory /> },
              { path: ROUTES.CRM_HELP_CATEGORIES, element: <CrmHelpCategories /> },
              { path: ROUTES.CRM_HELP_ARTICLES, element: <CrmHelpArticles /> },
              { path: ROUTES.CRM_SITE_PAGES, element: <CrmSitePages /> },
              { path: ROUTES.CRM_SUPPORT_WIDGET, element: <CrmSupportWidget /> },
              { path: ROUTES.REPORT_ANALYTICS, element: <AnalyticsReport /> },
              { path: ROUTES.REPORT_SCHEDULES, element: <ReportSchedules /> },
              { path: ROUTES.REPORT_SOLVENCY, element: <SolvencyReport /> },
              { path: ROUTES.REPORT_GAME, element: <GameReport /> },
              { path: ROUTES.REPORT_PAYMENT, element: <PaymentReport /> },
              { path: ROUTES.REPORT_WALLET_LEDGER, element: <WalletLedgerReport /> },
              { path: ROUTES.REPORT_BONUS_GRANT, element: <BonusGrantReport /> },
              { path: ROUTES.REPORT_BONUS_WALLET_LEDGER, element: <BonusWalletLedgerReport /> },
              { path: ROUTES.REPORT_DAILY_SPIN, element: <DailySpinReport /> },
              { path: ROUTES.REPORT_WAGER_RACE, element: <WagerRaceReport /> },
              { path: ROUTES.REPORT_SLOT_BATTLE, element: <SlotBattleReport /> },
              { path: ROUTES.REPORT_RAFFLE, element: <RaffleReport /> },
              { path: ROUTES.REPORT_RAKEBACK, element: <RakebackReport /> },
              { path: ROUTES.REPORT_VIP_BOOST, element: <VipBoostReport /> },
              { path: ROUTES.REPORT_DAILY_CHECKIN, element: <DailyCheckinReport /> },
              { path: ROUTES.REPORT_MISSION, element: <MissionReport /> },
              { path: ROUTES.REPORT_LOYALTY_STORE, element: <LoyaltyStoreReport /> },
              { path: ROUTES.REPORT_TIPPING, element: <TippingReport /> },
              { path: ROUTES.PROMOTION_TEMPLATE, element: <PromotionTemplate /> },
              { path: ROUTES.PROMOTION_VIP_LIST, element: <PromotionVipList /> },
              { path: ROUTES.PROMOTION_VIP_SOURCES, element: <PromotionVipSources /> },
              { path: ROUTES.PROMOTION_DAILY_SPIN, element: <PromotionDailySpin /> },
              { path: ROUTES.PROMOTION_JACKPOT, element: <PromotionJackpot /> },
              { path: ROUTES.PROMOTION_WAGER_RACE, element: <PromotionWagerRace /> },
              { path: ROUTES.PROMOTION_CHALLENGE, element: <PromotionChallenge /> },
              { path: ROUTES.PROMOTION_RAFFLE, element: <PromotionRaffle /> },
              { path: ROUTES.PROMOTION_RAKEBACK, element: <PromotionRakeback /> },
              { path: ROUTES.PROMOTION_VIP_BOOST, element: <PromotionVipBoost /> },
              { path: ROUTES.PROMOTION_VIP_HOST, element: <PromotionVipHost /> },
              { path: ROUTES.PROMOTION_DAILY_CHECKIN, element: <PromotionDailyCheckin /> },
              { path: ROUTES.PROMOTION_MISSION, element: <PromotionMission /> },
              { path: ROUTES.PROMOTION_LOYALTY_STORE, element: <PromotionLoyaltyStore /> },
              { path: ROUTES.PERMISSION_SIDEBAR, element: <SidebarManage /> },
              { path: ROUTES.PERMISSION_GROUPS, element: <GroupManage /> },
              { path: ROUTES.PERMISSION_FUNCTIONS, element: <FunctionsManage /> },
              { path: ROUTES.PERMISSION_ICONS, element: <Icons /> },
              { path: ROUTES.SESSION, element: <Sessions /> },
              { path: ROUTES.SECURITY_AUDIT_LOG, element: <AuditLog /> },
              { path: ROUTES.SECURITY_BACKUPS, element: <Backups /> },
              { path: ROUTES.SECURITY_LOGIN_HISTORY, element: <LoginHistory /> },
              { path: ROUTES.SECURITY_TRACKING_WHITELIST, element: <TrackingWhitelist /> },
              { path: ROUTES.SECURITY_GEO_ACCESS, element: <GeoAccess /> },
              { path: ROUTES.SECURITY_SERVER_SWITCHES, element: <ServerSwitches /> },
            ],
          },
        ],
      },
    ],
  },
  {
    errorElement: <RouteError />,
    path: ROUTES.LOGIN,
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <Login />
      </Suspense>
    ),
  },
  {
    errorElement: <RouteError />,
    path: ROUTES.NOT_FOUND,
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <NotFound />
      </Suspense>
    ),
  },
]);

export default router;
